import React from 'react';
import { FormControlLabel, Stack, Switch } from '@mui/material';
import type { DigestPreferences } from '../../api/config';

/**
 * The four per-section digest toggles (restock / daily_usage / oos / rma).
 * Restock is locked-on and RMA is locked-off — both reflect server-side
 * reality:
 *   - turning restock off silently makes the digest job no-op for that
 *     location, which AMs hit by accident in the old permissive dashboard UI
 *   - RMA digests aren't wired up server-side yet
 * If either constraint changes, update `LOCKED_KEYS` / `LOCKED_VALUES` here.
 */
const SECTION_KEYS = ['restock', 'daily_usage', 'oos', 'rma'] as const;
type SectionKey = (typeof SECTION_KEYS)[number];

const LOCKED_VALUES: Partial<Record<SectionKey, boolean>> = {
  restock: true,
  rma: false,
};

const SECTION_LABEL: Record<SectionKey, string> = {
  restock: 'Restock (Required)',
  daily_usage: 'Daily Usage',
  oos: 'Out of Stock',
  rma: 'RMA (Coming Soon)',
};

interface DigestPreferencesFieldsProps {
  value: Partial<DigestPreferences> | undefined;
  onChange: (patch: { digest_preferences: DigestPreferences }) => void;
  density?: 'standard' | 'compact';
}

// Server-side defaults from Location#default_digest_preferences. Used to
// promote a partial `digest_preferences` payload (e.g. an older row missing a
// field) to a fully-shaped `DigestPreferences` before emitting the patch.
const DEFAULT_PREFS: DigestPreferences = {
  restock: true,
  daily_usage: false,
  oos: false,
  rma: false,
};

export const DigestPreferencesFields: React.FC<DigestPreferencesFieldsProps> = ({
  value,
  onChange,
  density = 'standard',
}) => {
  const prefs = value ?? {};
  const switchSize = density === 'compact' ? 'small' : 'medium';

  const renderToggle = (key: SectionKey) => {
    const locked = key in LOCKED_VALUES;
    const checked = locked ? !!LOCKED_VALUES[key] : !!prefs[key];
    return (
      <FormControlLabel
        key={key}
        control={
          <Switch
            size={switchSize}
            checked={checked}
            disabled={locked}
            onChange={e => onChange({
              digest_preferences: { ...DEFAULT_PREFS, ...prefs, [key]: e.target.checked },
            })}
          />
        }
        label={SECTION_LABEL[key]}
        sx={density === 'compact' ? { mr: 1 } : undefined}
      />
    );
  };

  if (density === 'compact') {
    return (
      <Stack direction="row" spacing={0} flexWrap="wrap">
        {SECTION_KEYS.map(renderToggle)}
      </Stack>
    );
  }

  return <>{SECTION_KEYS.map(renderToggle)}</>;
};
